import React, { useState } from "react";
import PageShell from "../dashboard/PageShell";
import { Search, FileDown, BarChart3 } from "lucide-react";

interface StatisticsItem {
  id: string;
  stt: number;
  taskName: string;
  principal: string;
  field: string;
  managementLevel: string;
  coordinatingUnit: string;
  budget: string;
  fundingSource: string;
  implementationTime: string;
  code: string;
}

const mockData: StatisticsItem[] = [
  {
    id: "1",
    stt: 1,
    taskName: "Nghiên cứu ứng dụng AI trong quản lý tài nguyên nước",
    principal: "PGS.TS Nguyễn Văn Minh",
    field: "Công nghệ thông tin",
    managementLevel: "Cấp Viện",
    coordinatingUnit: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
    budget: "250.5 triệu",
    fundingSource: "Ngân sách sự nghiệp khoa học",
    implementationTime: "2020-2022",
    code: "KHCN-2020-001",
  },
  {
    id: "2",
    stt: 2,
    taskName: "Phát triển hệ thống cảnh báo lũ sớm",
    principal: "GS.TS Lê Thị Thanh",
    field: "Thủy văn",
    managementLevel: "Cấp Quốc gia",
    coordinatingUnit: "Viện Khoa học Thủy lợi Việt Nam",
    budget: "180.2 triệu",
    fundingSource: "Ngân sách nhà nước",
    implementationTime: "2019-2021",
    code: "KHCN-2019-002",
  },
  {
    id: "3",
    stt: 3,
    taskName:
      "Nghiên cứu xác định nguyên nhân và đề xuất giải pháp khoa học công nghệ nhằm ổn định các cửa sông và vùng bờ biển tỉnh Bình Thuận",
    principal: "ThS Nguyễn Văn An",
    field: "Môi trường",
    managementLevel: "Cấp Viện/ Chi nhánh",
    coordinatingUnit: "Viện Nước, Tưới Tiêu và Môi trường",
    budget: "100 triệu",
    fundingSource:
      "Ngân sách sự nghiệp khoa học, Ngân sách giao tự chủ cho viện",
    implementationTime: "2021-2022",
    code: "KHCN-2021-003",
  },
];

const OverviewStatistics: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [managementLevel, setManagementLevel] = useState("");
  const [researchField, setResearchField] = useState("");
  const [fromYear, setFromYear] = useState("");
  const [toYear, setToYear] = useState("2025");

  return (
    <PageShell title="Thống kê tổng quan">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Thống kê tổng quan
          </h1>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm thống kê"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="flex space-x-4 items-center">
              <select
                value={managementLevel}
                onChange={(e) => setManagementLevel(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
              >
                <option>- Chọn cấp quản lý -</option>
                <option value="cap-vien">Cấp Viện</option>
                <option value="cap-quoc-gia">Cấp Quốc gia</option>
                <option value="cap-bo">Cấp Bộ</option>
              </select>

              <select
                value={researchField}
                onChange={(e) => setResearchField(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
              >
                <option>- Chọn lĩnh vực nghiên cứu -</option>
                <option value="cong-nghe-thong-tin">Công nghệ thông tin</option>
                <option value="thuy-van">Thủy văn</option>
                <option value="moi-truong">Môi trường</option>
              </select>

              <div className="relative">
                <input
                  type="date"
                  value={fromYear}
                  onChange={(e) => setFromYear(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
                />
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={toYear}
                  onChange={(e) => setToYear(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
                />
              </div>
            </div>

            {/* Row 3 - Action buttons */}
            <div className="flex justify-start space-x-4">
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span>Thống kê</span>
              </button>

              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-64">
                    Tên nhiệm vụ KH&CN
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Chủ nhiệm
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Lĩnh vực
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Cấp quản lý
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Đơn vị phối hợp
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Kinh phí
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Nguồn kinh phí
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Thời gian thực hiện
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-32">
                    Mã số
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-16">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-64">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight underline">
                        {item.taskName} »
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-48">
                      {item.principal}
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-32">
                      {item.field}
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-32 text-center">
                      {item.managementLevel}
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-48">
                      <div className="leading-tight text-sm">
                        {item.coordinatingUnit}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-32">
                      {item.budget}
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-48">
                      <div className="leading-tight text-sm">
                        {item.fundingSource}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-32 text-center">
                      {item.implementationTime}
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 w-32">
                      {item.code}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">3</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default OverviewStatistics;
